import React, { createContext, useContext, useState, useEffect } from 'react';
import esTranslations from './locales/es.json';
import enTranslations from './locales/en.json';

const I18nContext = createContext();

const translations = {
  es: esTranslations,
  en: enTranslations,
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};

export const I18nProvider = ({ children }) => {
  // DEFAULT LANGUAGE IS ALWAYS ENGLISH ('en')
  // The application will always load in English by default for new users
  // Users can change the language manually, and their preference will be saved
  const DEFAULT_LANGUAGE = 'en';
  const supportedLanguages = ['en', 'es'];
  
  const [language, setLanguage] = useState(() => {
    // Check if user has a saved language preference
    const savedLanguage = localStorage.getItem('jiddufilms_language');
    
    // Only use saved language if it's a valid supported language
    if (savedLanguage && supportedLanguages.includes(savedLanguage)) {
      return savedLanguage;
    }
    
    // Always default to English for new users or invalid saved language
    return DEFAULT_LANGUAGE;
  });

  // Actualizar el atributo lang del HTML cuando cambia el idioma
  useEffect(() => {
    document.documentElement.lang = language;
    localStorage.setItem('jiddufilms_language', language);
  }, [language]);

  // Función para traducir
  const t = (key, params = {}) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Si no encuentra la traducción, intenta con inglés como fallback
        value = translations['en'];
        for (const fallbackKey of keys) {
          if (value && typeof value === 'object' && fallbackKey in value) {
            value = value[fallbackKey];
          } else {
            console.warn(`Translation key "${key}" not found`);
            return key;
          }
        }
        break;
      }
    }

    // Si el valor es un string, reemplazar parámetros si existen
    if (typeof value === 'string' && Object.keys(params).length > 0) {
      return value.replace(/\{\{(\w+)\}\}/g, (match, paramKey) => {
        return params[paramKey] !== undefined ? params[paramKey] : match;
      });
    }

    // Devolver el valor tal cual si es un array u otro tipo (no solo strings)
    return value !== undefined && value !== null ? value : key;
  };

  const changeLanguage = (lang) => {
    if (translations[lang]) {
      setLanguage(lang);
    } else {
      console.warn(`Language "${lang}" is not supported`);
    }
  };

  const value = {
    language,
    t,
    changeLanguage,
  };

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export default I18nProvider;

